import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type {
  PanelSummary,
  ResearchReport,
  ResearchSection,
} from '../experimentTypes';
import type {
  ResearchAcquisitionRow,
  ResearchAdsRow,
  ResearchCampaignContrastMetric,
  ResearchCampaignEconomicsRow,
  ResearchCampaignFunnelContrastRow,
  ResearchCreativeFunnelContrastRow,
  ResearchChampionRow,
  ResearchDataHealthRow,
  ResearchExitScreenRow,
  ResearchFirstScreenRow,
  ResearchLadderRow,
  ResearchMicroFunnelRow,
  ResearchCampaignCheckoutStagesRow,
  ResearchPriceMixRow,
  ResearchRowBase,
  ResearchRowDelta,
  ResearchSegmentDiscoveryRow,
  ResearchStat,
  ResearchWhatChangedRow,
} from '@modules/shared-types';
import { ExperimentsApiClient } from '../api/experimentsClient';
import { StatTierChip } from './StatTierChip';
import { EXPERIMENTS_TEST_IDS } from '../experimentsTestIds';

/**
 * E2c Phase B lazy detail dialog: renders the stored ResearchReport verbatim
 * (Spec D §7 — the FE NEVER computes; every rate arrives with its Wilson CI +
 * tier, every movement/excess-loss arrives tested). "What changed" is pinned
 * FIRST when a predecessor exists; the A–K catalog follows as accordions with
 * a generic table fallback for unknown section ids (additive-schema
 * forward-compat). Tables follow the DecisionScorecard idiom (static MUI
 * `Table size="small"` — render-only mini-tables, not interactive lists).
 */

interface ResearchReportDetailDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  reportId: string;
  /** E2c Phase C: latest panel run from the CARD payload (not the report JSON). */
  panel?: PanelSummary | null;
}

const PANEL_PERSONA_LABEL: Record<string, string> = {
  marketer: 'Marketer',
  funnel_architect: 'Funnel architect',
  numbers_analyst: 'Numbers analyst',
};

const PANEL_VERDICT_COLOR: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  supported: 'success',
  weak: 'default',
  contradicted: 'error',
  unverifiable: 'warning',
};

const PANEL_REJECT_COLOR: Record<string, 'warning' | 'error' | 'default'> = {
  underpowered: 'warning',
  dedup: 'default',
  dismissed: 'error',
};

/**
 * E2c Phase C: expert-panel block — a neutral bordered section pinned after
 * "What changed" (the dialog keeps ONE highlight). Render-only: verdicts and
 * counts arrive computed; failed personas and non-drafted proposals render
 * with their reasons (honesty over silence).
 */
const PanelSection: React.FC<{ panel: PanelSummary }> = ({ panel }) => (
  <Box
    sx={{ border: theme => `1px solid ${theme.palette.divider}`, borderRadius: 1, p: 2 }}
    data-testid={EXPERIMENTS_TEST_IDS.researchPanelSection}
  >
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
      <Typography variant="subtitle2">Expert panel</Typography>
      <Chip size="small" variant="outlined" label={panel.status} color={panel.status === 'ok' ? 'success' : 'warning'} />
      <Chip size="small" variant="outlined" label={panel.model} />
      <Chip size="small" variant="outlined" label={panel.generatedAt.slice(0, 10)} />
    </Stack>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
      3 personas in parallel, scientist filter after — numbers quoted from the report; sample sizes computed by the system.
    </Typography>
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Persona</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Headline</TableCell>
            <TableCell align="right">Hypotheses</TableCell>
            <TableCell align="right">Proposals</TableCell>
            <TableCell>Prompt</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {panel.personaSummaries.map(persona => (
            <TableRow key={persona.personaId} data-testid={EXPERIMENTS_TEST_IDS.researchPanelPersona(persona.personaId)}>
              <TableCell>{PANEL_PERSONA_LABEL[persona.personaId] ?? persona.personaId}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  variant="outlined"
                  label={persona.status}
                  color={persona.status === 'ok' ? 'success' : 'error'}
                />
              </TableCell>
              <TableCell sx={{ whiteSpace: 'normal' }}>{persona.headline ?? '—'}</TableCell>
              <TableCell align="right">{persona.hypothesisCount}</TableCell>
              <TableCell align="right">{persona.proposedExperimentCount}</TableCell>
              <TableCell>
                {persona.promptSource === 'override' ? (
                  <Chip size="small" variant="outlined" color="warning" label="override" />
                ) : persona.promptSource ? (
                  <Chip size="small" variant="outlined" label={`registry${persona.promptVersion ? ` v${persona.promptVersion}` : ''}`} />
                ) : (
                  '—'
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
      <Typography variant="caption" color="text.secondary">Scientist verdicts:</Typography>
      {panel.scientist.status === 'ok' ? (
        // Fixed significance order — Firestore map key order is not stable.
        (['supported', 'weak', 'contradicted', 'unverifiable'] as const).map(verdict => (
          <Chip
            key={verdict}
            size="small"
            variant="outlined"
            label={`${verdict} ${panel.scientist.verdictCounts[verdict] ?? 0}`}
            color={PANEL_VERDICT_COLOR[verdict] ?? 'default'}
          />
        ))
      ) : (
        <Chip size="small" variant="outlined" color="error" label="scientist failed — drafts unverified" />
      )}
    </Stack>
    {panel.draftExperimentIds.length > 0 && (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary">Drafts created:</Typography>
        <Chip size="small" label={`${panel.draftExperimentIds.length} draft experiment${panel.draftExperimentIds.length === 1 ? '' : 's'} in the hub`} />
      </Stack>
    )}
    {panel.rejectedProposals.length > 0 && (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary">Not drafted:</Typography>
        {panel.rejectedProposals.map(rejected => (
          <Chip
            key={`${rejected.proposalRef}-${rejected.reason}`}
            size="small"
            variant="outlined"
            color={PANEL_REJECT_COLOR[rejected.reason] ?? 'default'}
            label={`${rejected.reason} — ${rejected.proposalRef}${rejected.detail ? ` (${rejected.detail})` : ''}`}
            sx={{ maxWidth: 420 }}
          />
        ))}
      </Stack>
    )}
    {panel.deepDiveScreenIds.length > 0 && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {panel.deepDiveScreenIds.length} anomalous screen{panel.deepDiveScreenIds.length === 1 ? '' : 's'} auto-deep-dived into the panel grounding
      </Typography>
    )}
  </Box>
);

const HEALTH_COLOR: Record<string, 'success' | 'warning' | 'error'> = {
  pass: 'success',
  warn: 'warning',
  fail: 'error',
};

const STATUS_COLOR: Record<string, 'success' | 'warning' | 'error'> = {
  ok: 'success',
  degraded: 'warning',
  unavailable: 'error',
};

function formatPct(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${(value * 100).toFixed(digits)}%`;
}

function formatNum(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return value.toLocaleString('en-US');
}

function formatMoney(value: number | null | undefined, currency?: string | null): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const amount = Math.round(value).toLocaleString('en-US');
  if (currency && currency !== 'USD') return `${amount} ${currency}`;
  return `$${amount}`;
}

/**
 * AC-H-8: money cell honoring the row's typed spend-money state (donor:
 * FunnelReportsDashboard). mixed → withheld with the reason; missing →
 * magnitude kept label-free (recorded AC-H-9 deviation); single → labeled
 * with the row currency; legacy rows (no state) keep the historical render.
 */
function MoneyCell({ value, state, currency }: {
  value: number | null | undefined;
  state?: string | null;
  currency?: string | null;
}): React.ReactElement {
  const mixed = state === 'mixed_currency';
  const missing = state === 'missing_currency';
  if (!mixed && !missing) return <>{formatMoney(value, currency)}</>;
  return (
    <Box component="span" sx={{ display: 'inline-block' }}>
      <Typography variant="body2" component="span">
        {missing && typeof value === 'number' && Number.isFinite(value)
          ? Math.round(value).toLocaleString('en-US')
          : 'n/a'}
      </Typography>
      <Typography variant="caption" color="warning.main" sx={{ display: 'block', fontSize: '0.66rem', lineHeight: 1.2 }}>
        {mixed ? 'mixed ad-account currencies — sum withheld' : 'spend currency evidence missing'}
      </Typography>
    </Box>
  );
}

function formatP(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  if (value < 0.001) return '<0.001';
  return value.toFixed(3);
}

/** ResearchStat → the shared StatTierChip qualification shape (render-only). */
function StatCell({ stat, digits = 2 }: { stat: ResearchStat | null; digits?: number }): React.ReactElement {
  if (!stat || stat.rate === null) return <>—</>;
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="flex-end">
      <Typography variant="body2" component="span">{formatPct(stat.rate, digits)}</Typography>
      <StatTierChip
        qualification={{
          n: stat.denominator,
          p: stat.rate,
          ciLow: stat.ci?.[0] ?? 0,
          ciHigh: stat.ci?.[1] ?? 1,
          tier: stat.qualification,
        }}
        testId={`research-stat-tier-${stat.qualification}`}
      />
    </Stack>
  );
}

function formatRatio(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : `${value.toFixed(2)}×`;
}

/** Contrast metric cell: rate (baseline) + honesty label — mirror of the exit-screen vocabulary. */
function ContrastCell({ metric }: { metric: ResearchCampaignContrastMetric }): React.ReactElement {
  const negative = (metric.delta ?? 0) < 0;
  return (
    <Stack spacing={0} alignItems="flex-end">
      <Typography
        variant="body2"
        component="span"
        sx={metric.qualification === 'solid'
          ? { color: negative ? 'error.main' : 'success.main', fontWeight: 600 }
          : undefined}
      >
        {formatPct(metric.rate.rate, 1)}
        <Typography variant="caption" color="text.secondary" component="span">
          {metric.baselineRate !== null ? ` (base ${formatPct(metric.baselineRate, 1)})` : ''}
        </Typography>
      </Typography>
      <Chip
        size="small"
        variant="outlined"
        label={metric.qualification}
        color={metric.qualification === 'solid' ? (negative ? 'error' : 'success') : metric.qualification === 'directional' ? 'warning' : 'default'}
        sx={{ height: 16, fontSize: '0.6rem' }}
      />
    </Stack>
  );
}

function DeltaCell({ row }: { row: ResearchRowBase }): React.ReactElement {
  if (row.mark) return <Chip size="small" variant="outlined" label={row.mark} />;
  const delta: ResearchRowDelta | undefined = row.delta;
  if (!delta) return <>—</>;
  const sign = delta.rateDelta >= 0 ? '+' : '−';
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
      <Typography
        variant="caption"
        component="span"
        color={delta.qualification === 'insufficient' ? 'text.secondary' : delta.rateDelta >= 0 ? 'success.main' : 'error.main'}
      >
        {sign}{Math.abs(delta.rateDelta * 100).toFixed(2)}pp
      </Typography>
      <Chip size="small" variant="outlined" label={delta.qualification} color={
        delta.qualification === 'solid' ? 'success' : delta.qualification === 'directional' ? 'warning' : 'default'
      } />
    </Stack>
  );
}

interface ColumnSpec<TRow> {
  header: string;
  /** Dim second header line — allowed ONLY to name a differing unit / basis (never repeats the caption unit). */
  subheader?: string;
  align?: 'left' | 'right';
  render: (row: TRow) => React.ReactNode;
}

function SectionTable<TRow extends ResearchRowBase>({ rows, columns }: {
  rows: TRow[];
  columns: Array<ColumnSpec<TRow>>;
}): React.ReactElement {
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map(column => (
              <TableCell key={column.header} align={column.align ?? 'left'} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                {column.header}
                {column.subheader && (
                  <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block', fontWeight: 400 }}>
                    {column.subheader}
                  </Typography>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <TableRow key={row.rowKey}>
              {columns.map(column => (
                <TableCell key={column.header} align={column.align ?? 'left'} sx={{ whiteSpace: 'nowrap' }}>
                  {column.render(row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

const WHAT_CHANGED_COLUMNS: Array<ColumnSpec<ResearchWhatChangedRow>> = [
  { header: 'Kind', render: row => <Chip size="small" variant="outlined" label={row.kind.replace(/_/g, ' ')} color={row.kind === 'discovery_new' ? 'success' : 'default'} /> },
  { header: 'Section', render: row => row.sectionId.replace(/_/g, ' ') },
  { header: 'Item', render: row => row.label ?? row.targetRowKey },
  { header: 'Before', align: 'right', render: row => formatPct(row.before?.rate ?? null) },
  { header: 'After', align: 'right', render: row => formatPct(row.after?.rate ?? null) },
  { header: 'Δ / p / tier', align: 'right', render: row => <DeltaCell row={row} /> },
];

const SECTION_COLUMNS: Partial<Record<ResearchSection['id'], Array<ColumnSpec<never>>>> = {
  funnel_ladder: [
    { header: 'Stage', render: (row: ResearchLadderRow) => row.stage.replace(/_/g, ' ') },
    { header: 'Journeys', align: 'right', render: (row: ResearchLadderRow) => formatNum(row.journeys) },
    { header: 'Of started', align: 'right', render: (row: ResearchLadderRow) => <StatCell stat={row.ofStarted} /> },
    { header: 'Of previous', align: 'right', render: (row: ResearchLadderRow) => <StatCell stat={row.ofPrevious} /> },
    { header: 'Δ', align: 'right', render: (row: ResearchLadderRow) => <DeltaCell row={row} /> },
  ] as Array<ColumnSpec<never>>,
  acquisition: [
    { header: 'Source', render: (row: ResearchAcquisitionRow) => `${row.utmSource}${row.utmMedium ? ` / ${row.utmMedium}` : ''}` },
    { header: 'Journeys', align: 'right', render: (row: ResearchAcquisitionRow) => formatNum(row.journeys) },
    { header: 'Conv', align: 'right', render: (row: ResearchAcquisitionRow) => <StatCell stat={row.converted} /> },
    { header: 'Died on 1st', align: 'right', render: (row: ResearchAcquisitionRow) => formatPct(row.diedOnFirstScreen.rate, 1) },
    { header: 'Answer rate', align: 'right', render: (row: ResearchAcquisitionRow) => formatPct(row.answerRate.rate, 1) },
    { header: 'Conv (engaged)', align: 'right', render: (row: ResearchAcquisitionRow) => formatPct(row.convertedAmongEngaged.rate, 1) },
    { header: 'Median t on 1st', align: 'right', render: (row: ResearchAcquisitionRow) => row.medianTimeOnFirstScreenSeconds === null ? '—' : `${row.medianTimeOnFirstScreenSeconds}s` },
    { header: 'Δ conv', align: 'right', render: (row: ResearchAcquisitionRow) => <DeltaCell row={row} /> },
  ] as Array<ColumnSpec<never>>,
  ads: [
    { header: 'Level', render: (row: ResearchAdsRow) => <Chip size="small" variant="outlined" label={row.level} /> },
    { header: 'Entity', render: (row: ResearchAdsRow) => row.name ?? row.adId ?? row.adsetId ?? row.campaignId ?? row.rowKey },
    { header: 'Journeys', align: 'right', render: (row: ResearchAdsRow) => formatNum(row.journeys) },
    { header: 'Conv', align: 'right', render: (row: ResearchAdsRow) => <StatCell stat={row.converted} /> },
    { header: 'Spend', align: 'right', render: (row: ResearchAdsRow) => <MoneyCell value={row.spend} state={row.spendMoneyState} currency={row.moneyCurrency} /> },
    { header: 'CAC', align: 'right', render: (row: ResearchAdsRow) => <MoneyCell value={row.costPerOfferConversion} state={row.spendMoneyState} currency={row.moneyCurrency} /> },
    { header: 'Creative copy', render: (row: ResearchAdsRow) => row.creativeTitle ?? row.creativeBody ?? '—' },
  ] as Array<ColumnSpec<never>>,
  campaign_economics: [
    {
      header: 'Campaign',
      render: (row: ResearchCampaignEconomicsRow) => (
        <Stack spacing={0}>
          <Typography variant="body2" component="span">{row.campaign}</Typography>
          <Typography variant="caption" color="text.secondary">
            {[
              [row.utmSource, row.utmMedium].filter(Boolean).join(' / ') || null,
              row.subscriptions !== null ? `${formatNum(row.subscriptions)} subs` : null,
              row.cohortAgeDays !== null ? `${row.cohortAgeDays}d cohort` : null,
            ].filter(Boolean).join(' · ')}
          </Typography>
        </Stack>
      ),
    },
    { header: 'Spend', align: 'right', render: (row: ResearchCampaignEconomicsRow) => <MoneyCell value={row.spend} state={row.spendMoneyState} currency={row.moneyCurrency} /> },
    { header: 'CAC', align: 'right', render: (row: ResearchCampaignEconomicsRow) => <MoneyCell value={row.cac} state={row.spendMoneyState} currency={row.moneyCurrency} /> },
    { header: 'ROAS (covered)', align: 'right', render: (row: ResearchCampaignEconomicsRow) => formatRatio(row.roasCovered) },
    { header: 'ROAS (LTV)', align: 'right', render: (row: ResearchCampaignEconomicsRow) => formatRatio(row.ltvRoasCovered) },
    {
      header: 'pLTV₁₂ ROAS',
      align: 'right',
      render: (row: ResearchCampaignEconomicsRow) => row.pltv12Roas === null
        ? '—'
        : (
          <Stack spacing={0} alignItems="flex-end">
            <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>{formatRatio(row.pltv12Roas)}</Typography>
            <Typography variant="caption" color="text.secondary">
              {row.pltv12RoasP10 !== null && row.pltv12RoasP90 !== null
                ? `P10–P90 ${row.pltv12RoasP10.toFixed(2)}–${row.pltv12RoasP90.toFixed(2)}`
                : ''}
              {row.pltv12PerSub !== null ? ` · $${row.pltv12PerSub.toFixed(0)}/sub` : ''}
            </Typography>
          </Stack>
        ),
    },
    {
      header: 'Payback',
      align: 'right',
      render: (row: ResearchCampaignEconomicsRow) => row.paybackMonth !== null
        ? `~mo ${row.paybackMonth}`
        : row.paybackBeyondHorizon
          ? '> 12 mo'
          : row.pltv12Roas !== null ? 'never' : '—',
    },
    {
      header: 'Verdict',
      render: (row: ResearchCampaignEconomicsRow) => row.verdict === 'none'
        ? '—'
        : (
          <Chip
            size="small"
            variant="outlined"
            label={row.verdict === 'too_young'
              ? `too young${row.cohortAgeDays !== null ? ` (${row.cohortAgeDays}d)` : ''}`
              : row.verdict.toUpperCase()}
            color={row.verdict === 'kill' ? 'error' : row.verdict === 'scale' ? 'success' : row.verdict === 'watch' ? 'warning' : 'default'}
            sx={row.verdict === 'too_young' ? { borderStyle: 'dashed', color: 'text.disabled' } : undefined}
          />
        ),
    },
  ] as Array<ColumnSpec<never>>,
  campaign_funnel_contrast: [
    {
      header: 'Campaign',
      render: (row: ResearchCampaignFunnelContrastRow) => (
        <Stack spacing={0}>
          <Typography variant="body2" component="span">{row.campaign}</Typography>
          <Typography variant="caption" color="text.secondary">{formatNum(row.journeys)} journeys</Typography>
        </Stack>
      ),
    },
    { header: 'Past first screen', align: 'right', render: (row: ResearchCampaignFunnelContrastRow) => <ContrastCell metric={row.firstScreen} /> },
    { header: 'Quiz depth', align: 'right', render: (row: ResearchCampaignFunnelContrastRow) => <ContrastCell metric={row.quizDepth} /> },
    { header: 'Paywall reach', align: 'right', render: (row: ResearchCampaignFunnelContrastRow) => <ContrastCell metric={row.paywallReach} /> },
    { header: 'Paywall → purchase', align: 'right', render: (row: ResearchCampaignFunnelContrastRow) => <ContrastCell metric={row.paywallToPurchase} /> },
    {
      header: 'Reading',
      render: (row: ResearchCampaignFunnelContrastRow) => (
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal', display: 'block', maxWidth: 260 }}>
          {row.reading}
        </Typography>
      ),
    },
  ] as Array<ColumnSpec<never>>,
  creative_funnel_contrast: [
    {
      header: 'Creative',
      render: (row: ResearchCreativeFunnelContrastRow) => (
        <Stack spacing={0}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="body2" component="span">{row.adName ?? row.adId}</Typography>
            {row.flags.includes('clickbait_suspect') && (
              <Chip
                size="small"
                variant="outlined"
                color="warning"
                label="message mismatch?"
                title="Observational signal only: high click response with weaker downstream behavior."
                sx={{ height: 16, fontSize: '0.6rem' }}
              />
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {formatNum(row.journeys)} journeys{row.metaCampaignId ? ` · camp …${row.metaCampaignId.slice(-6)}` : ''}
          </Typography>
        </Stack>
      ),
    },
    { header: 'Past first screen', align: 'right', render: (row: ResearchCreativeFunnelContrastRow) => <ContrastCell metric={row.firstScreen} /> },
    { header: 'Quiz depth', align: 'right', render: (row: ResearchCreativeFunnelContrastRow) => <ContrastCell metric={row.quizDepth} /> },
    { header: 'Paywall reach', align: 'right', render: (row: ResearchCreativeFunnelContrastRow) => <ContrastCell metric={row.paywallReach} /> },
    { header: 'Paywall → purchase', align: 'right', render: (row: ResearchCreativeFunnelContrastRow) => <ContrastCell metric={row.paywallToPurchase} /> },
    {
      header: 'Meta CTR',
      align: 'right',
      render: (row: ResearchCreativeFunnelContrastRow) =>
        row.metaCtr === null || !row.ctrCoveredDays
          ? 'n/a'
          : `${(row.metaCtr * 100).toFixed(2)}%`,
    },
    {
      header: 'Reading',
      render: (row: ResearchCreativeFunnelContrastRow) => (
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal', display: 'block', maxWidth: 260 }}>
          {row.reading}
        </Typography>
      ),
    },
  ] as Array<ColumnSpec<never>>,
  exit_screens: [
    {
      header: 'Screen',
      render: (row: ResearchExitScreenRow) => (
        <Stack spacing={0}>
          <Typography variant="body2" component="span">{row.screenName ?? row.screenId}</Typography>
          {row.peerGroup && (
            <Typography variant="caption" color="text.secondary">
              {row.peerGroup.screenType} · {row.peerGroup.depthBucket} · {row.peerGroup.peerCount} peers ({row.peerGroup.pool.replace(/_/g, ' ')})
            </Typography>
          )}
        </Stack>
      ),
    },
    { header: 'Saw', align: 'right', render: (row: ResearchExitScreenRow) => formatNum(row.saw) },
    { header: 'Exits', align: 'right', render: (row: ResearchExitScreenRow) => formatNum(row.exits) },
    { header: 'Exit rate', align: 'right', render: (row: ResearchExitScreenRow) => <StatCell stat={row.exitRate} digits={1} /> },
    { header: 'Peer mean', align: 'right', render: (row: ResearchExitScreenRow) => formatPct(row.peerMeanExitRate, 1) },
    {
      header: 'Excess',
      align: 'right',
      render: (row: ResearchExitScreenRow) => row.excessLoss
        ? (
          <Typography variant="body2" component="span" color={row.excessLoss.delta > 0 && row.excessLoss.qualification === 'solid' ? 'error.main' : 'text.primary'}>
            {row.excessLoss.delta >= 0 ? '+' : '−'}{Math.abs(row.excessLoss.delta * 100).toFixed(1)}pp · p={formatP(row.excessLoss.pValue)}
          </Typography>
        )
        : '—',
    },
    {
      header: 'Flags',
      render: (row: ResearchExitScreenRow) => (
        <Stack direction="row" spacing={0.5}>
          {row.flags.includes('anomalous_loss') && <Chip size="small" variant="outlined" color="error" label="anomalous loss" />}
          {row.flags.includes('insufficient_peers') && <Chip size="small" variant="outlined" color="warning" label="insufficient peers" />}
        </Stack>
      ),
    },
  ] as Array<ColumnSpec<never>>,
  first_screen_census: [
    { header: 'Screen', render: (row: ResearchFirstScreenRow) => row.screenName ?? row.screenId },
    { header: 'Journeys', align: 'right', render: (row: ResearchFirstScreenRow) => formatNum(row.journeys) },
    { header: 'Share', align: 'right', render: (row: ResearchFirstScreenRow) => formatPct(row.share, 1) },
  ] as Array<ColumnSpec<never>>,
  champions: [
    { header: 'Trait', render: (row: ResearchChampionRow) => row.traitKey },
    { header: 'Value', render: (row: ResearchChampionRow) => row.label ?? row.value },
    { header: 'Users', align: 'right', render: (row: ResearchChampionRow) => formatNum(row.users) },
    { header: 'Buyers', align: 'right', render: (row: ResearchChampionRow) => formatNum(row.buyers) },
    { header: 'Buyer rate', align: 'right', render: (row: ResearchChampionRow) => <StatCell stat={row.buyerRate} digits={1} /> },
    { header: 'Shrunk', align: 'right', render: (row: ResearchChampionRow) => formatPct(row.shrunkBuyerRate, 1) },
    { header: 'Δ', align: 'right', render: (row: ResearchChampionRow) => <DeltaCell row={row} /> },
  ] as Array<ColumnSpec<never>>,
  price_mix: [
    { header: 'Price', render: (row: ResearchPriceMixRow) => row.priceId },
    { header: 'Product', render: (row: ResearchPriceMixRow) => row.productId ?? '—' },
    { header: 'Exposure', align: 'right', render: (row: ResearchPriceMixRow) => formatNum(row.exposure) },
    { header: 'Conv', align: 'right', render: (row: ResearchPriceMixRow) => <StatCell stat={row.converted} digits={1} /> },
    { header: 'Revenue', align: 'right', render: (row: ResearchPriceMixRow) => formatMoney(row.revenue) },
    { header: 'Share', align: 'right', render: (row: ResearchPriceMixRow) => formatPct(row.revenueShare, 1) },
  ] as Array<ColumnSpec<never>>,
  purchase_micro_funnel: [
    // E7 (AC-100): the human stage label leads; pre-E7 stored reports keep
    // rendering their raw event names.
    { header: 'Stage', render: (row: ResearchMicroFunnelRow) => row.stageLabel ?? row.eventName },
    { header: 'Journeys', align: 'right', render: (row: ResearchMicroFunnelRow) => formatNum(row.journeys) },
    { header: 'Of first stage', align: 'right', render: (row: ResearchMicroFunnelRow) => <StatCell stat={row.ofFirstStage} digits={1} /> },
    { header: 'Of previous', align: 'right', render: (row: ResearchMicroFunnelRow) => <StatCell stat={row.ofPrevious} digits={1} /> },
  ] as Array<ColumnSpec<never>>,
  campaign_checkout_stages: [
    // F2 (AC-103): E7 stages per campaign — journeys grain. The unit lives ONCE
    // in the summary caption; dim second header lines name only the basis of a
    // column (REVIEW P0-2: Paid > Opened is TRUE before the wallet-sheet event —
    // the explanation sits beside the numbers, not five lines away). Outcome
    // first, next to its denominator; the wallet tell right after it. Whole
    // percents: every tier here is directional at best, and the seven chip
    // columns must fit the xl dialog at the 1280 floor (REVIEW P0-1).
    {
      header: 'Campaign',
      render: (row: ResearchCampaignCheckoutStagesRow) => row.isOther
        ? <Typography variant="body2" component="span" color="text.secondary" sx={{ fontStyle: 'italic' }}>{`other · ${row.foldedCampaigns ?? 0} campaigns`}</Typography>
        : row.campaign,
    },
    { header: 'Pressed', subheader: 'denominator', align: 'right', render: (row: ResearchCampaignCheckoutStagesRow) => formatNum(row.pressed) },
    { header: 'Paid', subheader: '% of pressed', align: 'right', render: (row: ResearchCampaignCheckoutStagesRow) => <StatCell stat={row.paid} digits={0} /> },
    { header: 'Paid, no form', subheader: 'wallet, pre-sheet', align: 'right', render: (row: ResearchCampaignCheckoutStagesRow) => <StatCell stat={row.paidWithoutCheckout} digits={0} /> },
    { header: 'Opened', subheader: 'widget or sheet', align: 'right', render: (row: ResearchCampaignCheckoutStagesRow) => <StatCell stat={row.checkoutOpened} digits={0} /> },
    { header: 'Closed w/o pay', align: 'right', render: (row: ResearchCampaignCheckoutStagesRow) => <StatCell stat={row.closedWithoutPay} digits={0} /> },
    { header: 'Error', align: 'right', render: (row: ResearchCampaignCheckoutStagesRow) => <StatCell stat={row.paymentError} digits={0} /> },
  ] as Array<ColumnSpec<never>>,
  segment_discoveries: [
    { header: 'Segment', render: (row: ResearchSegmentDiscoveryRow) => `${row.axis}: ${row.label ?? row.value}${row.valueB ? ` × ${row.valueB}` : ''}` },
    { header: 'n', align: 'right', render: (row: ResearchSegmentDiscoveryRow) => formatNum(row.n) },
    { header: 'Rate (shrunk)', align: 'right', render: (row: ResearchSegmentDiscoveryRow) => formatPct(row.shrunkRate) },
    { header: 'vs rest', align: 'right', render: (row: ResearchSegmentDiscoveryRow) => formatPct(row.complementRate) },
    { header: 'adj. p', align: 'right', render: (row: ResearchSegmentDiscoveryRow) => formatP(row.adjustedP) },
    {
      header: 'Flag',
      render: (row: ResearchSegmentDiscoveryRow) => (
        <Chip
          size="small"
          variant="outlined"
          label={row.flag.replace(/_/g, ' ')}
          color={row.flag === 'discovery' ? 'success' : 'default'}
        />
      ),
    },
  ] as Array<ColumnSpec<never>>,
  data_health: [
    { header: 'Check', render: (row: ResearchDataHealthRow) => row.label },
    { header: 'Value', align: 'right', render: (row: ResearchDataHealthRow) => row.checkId.includes('hours') ? (row.value === null ? '—' : `${row.value.toFixed(1)}h`) : row.checkId === 'scope_nonempty' ? formatNum(row.value) : formatPct(row.value, 1) },
    { header: 'Status', render: (row: ResearchDataHealthRow) => <Chip size="small" variant="outlined" label={row.status} color={HEALTH_COLOR[row.status] ?? 'warning'} /> },
  ] as Array<ColumnSpec<never>>,
};

/** Forward-compat: unknown section ids render their scalar fields as-is. */
function genericColumns(rows: ResearchRowBase[]): Array<ColumnSpec<ResearchRowBase>> {
  const first = (rows[0] ?? {}) as unknown as Record<string, unknown>;
  const keys = Object.keys(first)
    .filter(key => ['string', 'number', 'boolean'].includes(typeof first[key]))
    .slice(0, 6);
  if (keys.length === 0) keys.push('rowKey');
  return keys.map(key => ({
    header: key,
    render: (row: ResearchRowBase) => {
      const value = (row as unknown as Record<string, unknown>)[key];
      return value === null || value === undefined ? '—' : String(value);
    },
  }));
}

function SectionAccordion({ section }: { section: ResearchSection }): React.ReactElement {
  const columns = SECTION_COLUMNS[section.id] as Array<ColumnSpec<ResearchRowBase>> | undefined;
  const rows = section.rows as unknown as ResearchRowBase[];
  // Prefer the server-computed headline count — the visible rows are capped,
  // so counting them would understate anomalies (honesty rule).
  const anomalies = section.id === 'exit_screens'
    ? Number(section.summary.anomalousScreens ?? rows.filter(row => row.flags.includes('anomalous_loss')).length)
    : 0;
  return (
    <Accordion disableGutters data-testid={EXPERIMENTS_TEST_IDS.researchSection(section.id)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ width: '100%' }}>
          <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 160 }}>{section.title}</Typography>
          <Chip size="small" variant="outlined" label={section.status} color={STATUS_COLOR[section.status] ?? 'warning'} />
          {anomalies > 0 && <Chip size="small" variant="outlined" color="error" label={`${anomalies} anomalies`} />}
          {section.truncated && (
            <Chip size="small" variant="outlined" label={`showing ${section.rows.length} of ${section.totalRowCount}`} />
          )}
          <Typography variant="caption" color="text.secondary">{section.rows.length} rows</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {section.status === 'unavailable' && (
          <Alert severity="warning" sx={{ mb: 1 }}>{section.statusReason ?? 'Section unavailable'}</Alert>
        )}
        {Object.keys(section.summary).length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {Object.entries(section.summary)
              .filter(([, value]) => value !== null && typeof value !== 'object')
              .map(([key, value]) => `${key}: ${typeof value === 'number' && !Number.isInteger(value) ? value.toFixed(4) : String(value)}`)
              .join(' · ')}
          </Typography>
        )}
        {rows.length > 0 && <SectionTable rows={rows} columns={columns ?? genericColumns(rows)} />}
        {section.truncated && section.drill && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Full rowset ({section.totalRowCount} rows) stored in the drill sidecar.
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

export const ResearchReportDetailDialog: React.FC<ResearchReportDetailDialogProps> = ({
  open,
  onClose,
  projectId,
  reportId,
  panel,
}) => {
  const [report, setReport] = useState<ResearchReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const retry = useCallback(() => setReloadKey(current => current + 1), []);

  useEffect(() => {
    if (!open || !projectId || !reportId) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    new ExperimentsApiClient()
      .getResearchReport(projectId, reportId, controller.signal)
      .then(loaded => setReport(loaded))
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Failed to load the report');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [open, projectId, reportId, reloadKey]);

  const whatChanged = report?.sections.find(section => section.id === 'what_changed');
  const catalogSections = report?.sections.filter(section => section.id !== 'what_changed') ?? [];
  const health = report?.meta.dataHealth;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth data-testid={EXPERIMENTS_TEST_IDS.researchDialog}>
      {/* ui-components invariant 1/2: close on the LEFT, centred title, single dismiss. */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.25, bgcolor: 'background.default', borderBottom: theme => `1px solid ${theme.palette.divider}` }}>
        <IconButton size="small" onClick={onClose} aria-label="Close"><CloseIcon fontSize="small" /></IconButton>
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <Typography variant="subtitle1">
            Research report{report ? ` · ${report.meta.window.from.slice(0, 10)} → ${report.meta.window.to.slice(0, 10)}` : ''}
          </Typography>
          {report && (
            <Typography variant="caption" color="text.secondary">
              series {report.meta.seriesId} · {report.reportId} · generated {report.meta.generatedAt.slice(0, 16).replace('T', ' ')}
            </Typography>
          )}
        </Box>
        <Box sx={{ width: 34 }} />
      </Box>

      <DialogContent>
        {loading && (
          <Stack alignItems="center" spacing={2} sx={{ py: 8 }} data-testid={EXPERIMENTS_TEST_IDS.researchDialogLoading}>
            <CircularProgress size={36} />
            <Typography variant="body2" color="text.secondary">Loading the stored report…</Typography>
          </Stack>
        )}

        {!loading && error && (
          <Alert
            severity="error"
            data-testid={EXPERIMENTS_TEST_IDS.researchDialogError}
            action={<Button size="small" data-testid={EXPERIMENTS_TEST_IDS.researchDialogRetry} onClick={retry}>Retry</Button>}
          >
            {error}
          </Alert>
        )}

        {!loading && !error && report && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              {health && (
                <Chip size="small" variant="outlined" label={`health: ${health.status}`} color={HEALTH_COLOR[health.status] ?? 'warning'} />
              )}
              {health?.checks.map(check => (
                <Chip
                  key={check.checkId}
                  size="small"
                  variant="outlined"
                  label={check.checkId.replace(/_/g, ' ')}
                  color={HEALTH_COLOR[check.status] ?? 'warning'}
                />
              ))}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              <b>{formatNum(report.meta.scope.journeys)}</b> journeys · <b>{formatNum(report.meta.scope.users)}</b> users ·{' '}
              <b>{formatNum(report.meta.scope.conversions)}</b> conversions · outcome {report.meta.config.params.outcome} · {report.meta.config.params.grain} grain
            </Typography>

            {whatChanged && (
              <Box
                sx={{
                  borderLeft: theme => `3px solid ${theme.palette.primary.main}`,
                  bgcolor: theme => alpha(theme.palette.primary.main, 0.06),
                  borderRadius: 1,
                  p: 2,
                }}
                data-testid={EXPERIMENTS_TEST_IDS.researchSection('what_changed')}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle2">What changed</Typography>
                  {report.meta.previousReportId && (
                    <Chip size="small" variant="outlined" label={`vs ${report.meta.previousReportId}`} />
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {Object.entries(whatChanged.summary)
                    .filter(([key]) => key !== 'previousReportId')
                    .map(([key, value]) => `${String(value)} ${key.replace(/([A-Z])/g, ' $1').toLowerCase()}`)
                    .join(' · ')}
                </Typography>
                <SectionTable rows={whatChanged.rows as ResearchWhatChangedRow[]} columns={WHAT_CHANGED_COLUMNS} />
                {whatChanged.truncated && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    showing {whatChanged.rows.length} of {whatChanged.totalRowCount} changes
                  </Typography>
                )}
              </Box>
            )}

            {panel && <PanelSection panel={panel} />}

            <Box>
              {catalogSections.map(section => (
                <SectionAccordion key={section.id} section={section} />
              ))}
            </Box>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};
